/**
 * Available operator types for different data comparisons
 */
export enum OperatorType {
	EQUALS = 'EQUALS',
	BOOLEAN = 'BOOLEAN',
	DOES_NOT_EQUAL = 'DOES_NOT_EQUAL',
	GREATER_THAN = 'GREATER_THAN',
	LESS_THAN = 'LESS_THAN',
	BETWEEN = 'BETWEEN',
	BEFORE = 'BEFORE',
	AFTER = 'AFTER',
	DAYS_SINCE = 'DAYS_SINCE',
	YEARS_SINCE = 'YEARS_SINCE',
	CONTAINS = 'CONTAINS',
	DOES_NOT_CONTAIN = 'DOES_NOT_CONTAIN',
}

/**
 * Base interface for all operators
 */
export abstract class Operator {
	readonly type!: OperatorType;
	readonly display!: string;
	/**
	 * @deprecated This property is maintained for backwards compatibility but should not be used in new code.
	 */
	readonly valueType: string;

	constructor(valueType: string) {
		this.valueType = valueType;
	}

	toJson(): string {
		return JSON.stringify({
			type: this.type,
			display: this.display,
			valueType: this.valueType,
		});
	}
}

/**
 * Operator for equality comparison
 */
export class EqualsOperator extends Operator {
	readonly type = OperatorType.EQUALS;
	readonly display = 'EQUALS';
}

/**
 * Operator for boolean values
 */
export class BooleanOperator extends Operator {
	readonly type = OperatorType.BOOLEAN;
	readonly display = 'BOOLEAN';
}

/**
 * Operator for inequality comparison
 */
export class DoesNotEqualOperator extends Operator {
	readonly type = OperatorType.DOES_NOT_EQUAL;
	readonly display = 'DOES NOT EQUAL';
}

/**
 * Operator for greater than comparison
 */
export class GreaterThanOperator extends Operator {
	readonly type = OperatorType.GREATER_THAN;
	readonly display = 'GREATER THAN';
}

/**
 * Operator for less than comparison
 */
export class LessThanOperator extends Operator {
	readonly type = OperatorType.LESS_THAN;
	readonly display = 'LESS THAN';
}

/**
 * Operator for range comparison
 */
export class BetweenOperator extends Operator {
	readonly type = OperatorType.BETWEEN;
	readonly display = 'BETWEEN';
}

/**
 * Operator for before comparison (typically for dates/years)
 */
export class BeforeOperator extends Operator {
	readonly type = OperatorType.BEFORE;
	readonly display = 'BEFORE';
}

/**
 * Operator for after comparison (typically for dates/years)
 */
export class AfterOperator extends Operator {
	readonly type = OperatorType.AFTER;
	readonly display = 'AFTER';
}

/**
 * Operator for date comparisons based on days elapsed
 */
export class DaysSinceOperator extends Operator {
	readonly type = OperatorType.DAYS_SINCE;
	readonly display = 'DAYS_SINCE';
}

/**
 * Operator for date comparisons based on years elapsed
 */
export class YearsSinceOperator extends Operator {
	readonly type = OperatorType.YEARS_SINCE;
	readonly display = 'YEARS_SINCE';
}

/**
 * Operator for text contains
 */
export class ContainsOperator extends Operator {
	readonly type = OperatorType.CONTAINS;
	readonly display = 'CONTAINS';
}

/**
 * Operator for text does not contain
 */
export class DoesNotContainOperator extends Operator {
	readonly type = OperatorType.DOES_NOT_CONTAIN;
	readonly display = 'DOES NOT CONTAIN';
}

/**
 * Base class for all Operators classes
 */
export abstract class BaseOperators {
	abstract readonly _type: string;
	protected readonly operators: Map<OperatorType, Operator>;

	constructor(supportedOperators: Operator[]) {
		this.operators = new Map<OperatorType, Operator>();
		supportedOperators.forEach(operator => {
			this.operators.set(operator.type, operator);
		});
	}

	get(type: OperatorType): Operator | undefined {
		return this.operators.get(type);
	}

	supports(type: OperatorType): boolean {
		return this.operators.has(type);
	}

	asList(): Operator[] {
		return Array.from(this.operators.values());
	}

	asObject(): Record<string, Operator> {
		return Object.fromEntries(this.operators);
	}

	toJson(): string {
		return JSON.stringify({
			operators: this.asList().map(op => ({
				type: op.type,
				display: op.display,
				valueType: op.valueType,
			})),
		});
	}
}

/**
 * Class representing operators for text properties
 */
export class TextOperators extends BaseOperators {
	readonly _type = 'text';

	constructor(subType?: string) {
		super(TextOperators.getOperators(subType));
	}

	private static getOperators(subType?: string): Operator[] {
		// Fields with the SubType 'Unique' will not be cached so the options will be a freeform text input
		if (subType === 'Unique') {
			return [
				new ContainsOperator('text'),
				new DoesNotContainOperator('text'),
				new EqualsOperator('text'),
				new DoesNotEqualOperator('text'),
			];
		}
		// Fields without subType will be cached and so the options will appear in the dropdown
		return [
			new EqualsOperator('multi-select'),
			new DoesNotEqualOperator('multi-select'),
		];
	}
}

/**
 * Class representing operators for numeric properties
 */
export class NumericOperators extends BaseOperators {
	readonly _type = 'numeric';

	constructor() {
		super([
			new EqualsOperator('numeric-text'),
			new DoesNotEqualOperator('numeric-text'),
			new GreaterThanOperator('numeric-text'),
			new LessThanOperator('numeric-text'),
			new BetweenOperator('numeric-text-two-fields'),
		]);
	}
}

/**
 * Class representing operators for currency properties
 */
export class CurrencyOperators extends BaseOperators {
	readonly _type = 'currency';

	constructor() {
		super([
			new EqualsOperator('currency-text'),
			new DoesNotEqualOperator('currency-text'),
			new GreaterThanOperator('currency-text'),
			new LessThanOperator('currency-text'),
			new BetweenOperator('currency-text-two-fields'),
		]);
	}
}

/**
 * Class representing operators for boolean properties
 */
export class BooleanOperators extends BaseOperators {
	readonly _type = 'boolean';

	constructor() {
		super([new BooleanOperator('boolean')]);
	}
}

/**
 * Class representing operators for date/time properties
 */
export class DateTimeOperators extends BaseOperators {
	readonly _type = 'date-time';

	constructor(subType?: string) {
		super(DateTimeOperators.getOperators(subType));
	}

	private static getOperators(subType?: string): Operator[] {
		if (subType === 'Year') {
			// For Year subtype: use year-picker operators plus YEARS_SINCE
			return [
				new EqualsOperator('year-picker'),
				new DoesNotEqualOperator('year-picker'),
				new BeforeOperator('year-picker'),
				new AfterOperator('year-picker'),
				new BetweenOperator('year-picker-two-fields'),
				new YearsSinceOperator('years-since'),
			];
		}

		// For other subtypes: use standard calendar operators
		return [
			new EqualsOperator('calendar-picker'),
			new DoesNotEqualOperator('calendar-picker'),
			new GreaterThanOperator('calendar-picker'),
			new LessThanOperator('calendar-picker'),
			new BetweenOperator('calendar-picker-two-fields'),
			new DaysSinceOperator('days-since'),
		];
	}
}

export type Operators =
	| TextOperators
	| NumericOperators
	| CurrencyOperators
	| BooleanOperators
	| DateTimeOperators;
